/* =============================================================
   StudentNotificationBell.jsx — Student portal notification bell
   -------------------------------------------------------------
   Generates notifications from data already in memory —
   zero extra Supabase queries.

   Data sources (all from module-level caches, instant):
     useStudentContext()  — student identity + exchange type
     useProcessConfig()   — required documents list
     useDocuments()       — reads uploadsCache directly
                            (does NOT trigger a new fetch)

   Why useDocuments() instead of useStudentDocuments()?
     useStudentDocuments() calls loadUploadsForStudent() which
     would start an extra Supabase fetch. The bell should be a
     passive reader: it shows what's already in cache, and the
     page components handle loading the data when needed.

   Notification types (per required document):
     rejected (priority 100) — IO rejected, student must re-upload
     missing  (priority  80) — not uploaded yet
     approved (priority  60) — IO approved, student informed
     pending  (priority  30) — uploaded, waiting for IO review
                               summarised as 1 notification for all

   Read state: localStorage, keyed by notification ID.
   IDs are deterministic (doc-name-based) so consistent across
   sessions. When a doc changes status (e.g., rejected → pending
   after re-upload), the old ID disappears and the new one is
   unread — correct behaviour.
   ============================================================= */

import { useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStudentContext }   from '../hooks/useStudentContext';
import { useProcessConfig }   from '../hooks/useProcessConfig';
import { useStudentTemplate } from '../hooks/useStudentTemplate';
import { useDocuments }       from '../context/DocumentContext';


/* ─── READ STATE ─────────────────────────────────────────────── */
const READ_KEY = 'unixplore_student_notif_read_v2';
function loadRead()    { try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]')); } catch { return new Set(); } }
function saveRead(set) { try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch {} }

/* ─── ID HELPER ──────────────────────────────────────────────── */
const toId = (prefix, name) =>
  `${prefix}-${name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;


/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function StudentNotificationBell() {
  const navigate = useNavigate();
  const ref      = useRef(null);
  const [params] = useSearchParams();

  const { student, studentId } = useStudentContext();
  const asParam = params.get('as');

  // Resolve the process template for this student, then load its steps
  const { templateId } = useStudentTemplate(student);
  // useProcessConfig uses module-level cache → returns instantly on re-navigation
  const { steps } = useProcessConfig(templateId);

  // Read directly from DocumentContext cache — NO new fetch triggered
  const { uploadsCache } = useDocuments();
  const documents = uploadsCache[studentId] ?? [];

  const [open,    setOpen]    = useState(false);
  const [readIds, setReadIds] = useState(() => loadRead());
  const leaveTimer = useRef(null);

  // Navigation that preserves ?as= param in admin preview mode
  const goTo = (path) => navigate(asParam ? `${path}?as=${asParam}` : path);

  const handleMouseEnter = () => { clearTimeout(leaveTimer.current); setOpen(true); };
  const handleMouseLeave = () => { leaveTimer.current = setTimeout(() => setOpen(false), 150); };


  /* ── Build notifications from in-memory data ───────────────── */
  const notifs = useMemo(() => {
    if (!studentId || steps.length === 0) return [];

    const generated = [];
    let pendingCount = 0;

    // One pass through required documents
    const seen = new Set();
    steps.forEach(step => {
      if (!step.document_types) return;

      const docName = step.document_types.name;
      if (seen.has(docName)) return; // skip duplicate step→docType mappings
      seen.add(docName);

      const upload = documents.find(d => d.docName === docName);
      const status = upload?.status ?? 'missing';

      if (status === 'rejected') {
        generated.push({
          id:        toId('rejected', docName),
          icon:      'cancel',
          iconBg:    'bg-red-100',
          iconColor: 'text-red-600',
          title:     `${docName}: changes requested`,
          desc:      'Please re-upload — IO requested a revision',
          route:     '/student/documents',
          priority:  100,
        });
      } else if (status === 'missing') {
        generated.push({
          id:        toId('missing', docName),
          icon:      'upload_file',
          iconBg:    'bg-red-100',
          iconColor: 'text-red-600',
          title:     `${docName} missing`,
          desc:      'Required document — upload now',
          route:     '/student/documents',
          priority:  80,
        });
      } else if (status === 'pending') {
        // Count pending docs but don't generate one per doc —
        // they get summarised into a single notification below
        pendingCount++;
      } else if (status === 'approved') {
        generated.push({
          id:        toId('approved', docName),
          icon:      'task_alt',
          iconBg:    'bg-green-100',
          iconColor: 'text-green-600',
          title:     `${docName} approvato`,
          desc:      'Documento approvato dall\'International Office',
          route:     '/student/documents',
          priority:  60,
        });
      }
    });

    // Single summary notification for all pending docs
    if (pendingCount > 0) {
      generated.push({
        id:        'pending-docs-summary',
        icon:      'hourglass_empty',
        iconBg:    'bg-amber-100',
        iconColor: 'text-amber-700',
        title:     pendingCount === 1
          ? '1 document pending review'
          : `${pendingCount} documents pending review`,
        desc:      'Uploaded · Waiting for IO approval',
        route:     '/student/documents',
        priority:  30,
      });
    }

    return generated.sort((a, b) => b.priority - a.priority);
  }, [studentId, steps, documents]);

  const unreadCount = notifs.filter(n => !readIds.has(n.id)).length;


  const markAllRead = () => {
    const updated = new Set(notifs.map(n => n.id));
    setReadIds(updated); saveRead(updated);
  };

  const handleClick = (notif) => {
    const updated = new Set([...readIds, notif.id]);
    setReadIds(updated); saveRead(updated);
    setOpen(false);
    goTo(notif.route);
  };


  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >

      {/* Bell */}
      <button
        className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors relative"
      >
        <span className="material-symbols-outlined text-[20px]">notifications</span>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-[10px] font-bold text-white px-0.5">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50">

          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-sm font-semibold text-[#0b1c30]">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
            {notifs.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <span className="material-symbols-outlined text-slate-200 text-[36px]">task_alt</span>
                <p className="text-xs font-medium text-slate-500 mt-2">All documents in order!</p>
                <p className="text-[11px] text-slate-400 mt-0.5">No action required right now.</p>
              </div>
            ) : (
              notifs.map(notif => {
                const isRead = readIds.has(notif.id);
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${!isRead ? 'bg-indigo-50/40' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-lg ${notif.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <span className={`material-symbols-outlined text-[16px] ${notif.iconColor}`}>{notif.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug text-[#0b1c30] ${!isRead ? 'font-bold' : 'font-medium'}`}>
                        {notif.title}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5 truncate">{notif.desc}</p>
                    </div>
                    {!isRead && <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-slate-100">
            <p className="text-[11px] text-slate-400 text-center">
              {unreadCount === 0
                ? 'All caught up!'
                : `${unreadCount} item${unreadCount > 1 ? 's' : ''} need your attention`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
